export type CitationEntry = Record<string, string>;
export type ComparisonStatus = "pending" | "processing" | "completed" | "failed";

export type CodeLibraryMunicipality = {
  id: number;
  name: string;
  state?: string | null;
};

export type CompareResult = {
  prompt: string;
  home_municipality: CodeLibraryMunicipality;
  comparison_municipality: CodeLibraryMunicipality;
  home: {
    answer?: string;
    reasoning?: string;
    citations: CitationEntry[];
  };
  comparison: {
    answer?: string;
    reasoning?: string;
    citations: CitationEntry[];
  };
  summary: {
    /** Comparisons saved before ENG-2280 only: markdown that repeats the lists below. */
    content?: string;
    /** Plain text; newer comparisons mark sources as [n], numbered home citations first. */
    summary: string;
    differences: string[];
    similarities: string[];
    citations: CitationEntry[];
  };
  generated_at: string;
};

export type ComparisonProgress = {
  home_researched_at: string | null;
  comparison_researched_at: string | null;
};

export type ComparisonJob = {
  id: number;
  status: ComparisonStatus;
  prompt: string;
  home_municipality: CodeLibraryMunicipality;
  comparison_municipality: CodeLibraryMunicipality;
  progress: ComparisonProgress;
  created_at: string;
  updated_at: string;
  result?: CompareResult;
  /** Present on completed comparisons. */
  feedback_state?: ComparisonFeedbackState;
  error?: string;
};

export type ComparisonFeedbackState = {
  thumb_state: "helpful" | "unhelpful" | null;
  has_feedback: boolean;
};

export type ComparisonHistoryPage = {
  comparisons: ComparisonJob[];
  pagination: {
    current_page: number;
    total_pages: number;
    per_page: number;
    total_count: number;
  };
};

export async function listCodeLibraryComparisons(page = 1): Promise<ComparisonHistoryPage> {
  const resp = await fetch(`/api/v1/code_library/comparisons?page=${page}&per_page=25`, {
    method: "GET",
    headers: {
      Accept: "application/json",
    },
    credentials: "same-origin",
  });

  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) {
    throw new Error(data.error || `Request failed with ${resp.status}`);
  }
  return data as ComparisonHistoryPage;
}

export async function getCodeLibraryIncludedMunicipalities(
  homeMunicipalityId: number
): Promise<CodeLibraryMunicipality[]> {
  const resp = await fetch(`/api/v1/code_library/municipalities/${homeMunicipalityId}/included`, {
    method: "GET",
    headers: {
      Accept: "application/json",
    },
    credentials: "same-origin",
  });

  const data = await resp.json().catch(() => ([]));
  if (!resp.ok) {
    throw new Error((data as {error?: string}).error || `Request failed with ${resp.status}`);
  }
  return data as CodeLibraryMunicipality[];
}

export async function createCodeLibraryComparison(params: {
  libraryMunicipalityId: number;
  homeMunicipalityId: number;
  comparisonMunicipalityId: number;
  prompt: string;
}): Promise<ComparisonJob> {
  const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "";
  const resp = await fetch("/api/v1/code_library/comparisons", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-CSRF-Token": csrf,
    },
    credentials: "same-origin",
    body: JSON.stringify({
      comparison: {
        library_municipality_id: params.libraryMunicipalityId,
        home_municipality_id: params.homeMunicipalityId,
        comparison_municipality_id: params.comparisonMunicipalityId,
        prompt: params.prompt,
      },
    }),
  });

  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) {
    throw new Error(data.error || `Request failed with ${resp.status}`);
  }
  return data as ComparisonJob;
}

export async function getCodeLibraryComparison(comparisonId: number): Promise<ComparisonJob> {
  const resp = await fetch(`/api/v1/code_library/comparisons/${comparisonId}`, {
    method: "GET",
    headers: {
      Accept: "application/json",
    },
    credentials: "same-origin",
  });

  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) {
    throw new Error(data.error || `Request failed with ${resp.status}`);
  }
  return data as ComparisonJob;
}

async function sendComparisonFeedbackRequest(
  path: string,
  method: "POST" | "DELETE",
  body?: unknown,
): Promise<ComparisonFeedbackState> {
  const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "";
  const resp = await fetch(path, {
    method,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-CSRF-Token": csrf,
    },
    credentials: "same-origin",
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) {
    throw new Error(data.error || `Request failed with ${resp.status}`);
  }
  return data.feedback_state as ComparisonFeedbackState;
}

export function submitCodeLibraryComparisonFeedback(
  comparisonId: number,
  feedback: { entryPoint: "thumbs_up" | "thumbs_down" | "feedback"; reasonCode?: string; detail?: string | null },
): Promise<ComparisonFeedbackState> {
  return sendComparisonFeedbackRequest(`/api/v1/code_library/comparisons/${comparisonId}/feedbacks`, "POST", {
    feedback: {
      entry_point: feedback.entryPoint,
      reason_code: feedback.reasonCode,
      detail: feedback.detail ?? undefined,
    },
  });
}

export function clearCodeLibraryComparisonRating(comparisonId: number): Promise<ComparisonFeedbackState> {
  return sendComparisonFeedbackRequest(`/api/v1/code_library/comparisons/${comparisonId}/rating`, "DELETE");
}
